import React from "react";
import { Download, AlertCircle, Activity, ShieldAlert } from "lucide-react";

const UploadStep = ({
  t,
  currentTheme,
  modalStyles,
  importFile,
  importError,
  importLoading,
  importSettings,
  setImportSettings,
  handleFileSelect,
  disclaimerAcknowledged,
  setDisclaimerAcknowledged,
  isAr,
}) => {
  return (
    <div>
      {/* ── Hidden real file input ── */}
      <input
        type="file"
        id="excel-file-input"
        accept=".xlsx,.xls,.csv"
        onChange={handleFileSelect}
        style={{ display: "none" }}
        disabled={!disclaimerAcknowledged}
      />

      {/* ── Responsibility Disclaimer (must be acknowledged before upload) ── */}
      <div
        style={{
          marginBottom: "20px",
          padding: "20px",
          borderRadius: "12px",
          border: `2px solid ${disclaimerAcknowledged ? currentTheme.colors.border : "#f59e0b"}`,
          background: disclaimerAcknowledged
            ? `${currentTheme.colors.inputBackground}`
            : "rgba(245,158,11,0.07)",
          transition: "all 0.3s ease",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "14px",
          }}
        >
          <ShieldAlert size={20} color="#f59e0b" />
          <strong
            style={{
              color: currentTheme.colors.textPrimary,
              fontSize: "15px",
              fontWeight: "700",
              letterSpacing: "0.3px",
            }}
          >
            {isAr
              ? "إشعار مسؤولية — رواتب مستوردة"
              : "Imported Payroll — User Responsibility Notice"}
          </strong>
        </div>

        {/* Body */}
        <div
          style={{
            color: currentTheme.colors.textSecondary,
            fontSize: "13px",
            lineHeight: "1.75",
            marginBottom: "14px",
          }}
        >
          {isAr ? (
            <>
              القيم الواردة في ملف Excel لها الأولوية القصوى وتُعتمد كما هي{" "}
              <strong>دون تعديل</strong>. أي قيم جوهرية <strong>غير موجودة في الملف</strong>{" "}
              (كالضرائب، صندوق الشهداء، الراتب الإجمالي، أو إجماليات البدلات والخصومات){" "}
              <strong>سيقوم النظام بحسابها تلقائيًا</strong> وفق القواعد المعتمدة.
              <br />
              <strong style={{ color: "#f59e0b" }}>⚡ تنبيه:</strong>{" "}
              القيم المستوردة تعلو على حسابات النظام، ويتحمل مستخدم الاستيراد المسؤولية الكاملة
              عن صحتها.{" "}
              <strong>نكسبلوي غير مسؤولة عن أخطاء حسابية ناتجة عن بيانات مستوردة.</strong>
              <br />
              بعد الاستيراد يصبح الراتب <strong>مقفلًا</strong>: يُستبدل الراتب الموجود لنفس الشهر ببيانات الملف،
              ولا يعيد النظام حسابه أو يعدله بعد ذلك إلا حالة الدفع.
            </>
          ) : (
            <>
              Values present in the imported Excel file take <strong>strict priority</strong> and are
              used as-is <strong>without modification</strong>. Any essential figures{" "}
              <strong>not present in the file</strong> (such as income tax, Martyrs&apos; Fund, gross
              salary, or allowance/deduction totals) <strong>will be automatically calculated</strong> by
              the system using official calculation rules.
              <br />
              <strong style={{ color: "#f59e0b" }}>⚡ Important:</strong> Imported figures override
              system defaults. The importing user bears <strong>full responsibility</strong> for the
              accuracy of all values provided in the file. Nexploy is not liable for miscalculations
              arising from imported data.
              <br />
              After import the payroll is <strong>locked</strong>: an existing payroll for the same month is replaced
              by the file&apos;s data, and nothing recalculates or changes it afterwards except its payment status.
            </>
          )}
        </div>

        {/* Acknowledgement checkbox */}
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            cursor: "pointer",
            padding: "10px 14px",
            borderRadius: "8px",
            background: disclaimerAcknowledged
              ? `${currentTheme.colors.success}15`
              : "rgba(245,158,11,0.1)",
            border: `1px solid ${
              disclaimerAcknowledged
                ? currentTheme.colors.success
                : "#f59e0b"
            }`,
            transition: "all 0.2s ease",
          }}
        >
          <input
            type="checkbox"
            checked={disclaimerAcknowledged}
            onChange={(e) => setDisclaimerAcknowledged(e.target.checked)}
            style={{ marginTop: "2px", accentColor: currentTheme.colors.success, width: "16px", height: "16px", cursor: "pointer" }}
          />
          <span
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: disclaimerAcknowledged
                ? currentTheme.colors.success
                : "#f59e0b",
              lineHeight: "1.5",
            }}
          >
            {isAr
              ? "لقد قرأت وأقبل المسؤولية الكاملة عن دقة جميع القيم المستوردة."
              : "I have read and accept full responsibility for the accuracy of all imported values."}
          </span>
        </label>
      </div>

      {/* ── Upload area — disabled until disclaimer is accepted ── */}
      <label
        htmlFor={disclaimerAcknowledged ? "excel-file-input" : undefined}
        style={{
          ...modalStyles.uploadArea,
          opacity: disclaimerAcknowledged ? 1 : 0.45,
          cursor: disclaimerAcknowledged ? "pointer" : "not-allowed",
          pointerEvents: disclaimerAcknowledged ? "auto" : "none",
        }}
        onMouseEnter={(e) => {
          if (!disclaimerAcknowledged) return;
          e.currentTarget.style.borderColor = currentTheme.colors.primary;
          e.currentTarget.style.background = `${currentTheme.colors.primary}15`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = currentTheme.colors.border;
          e.currentTarget.style.background = `${currentTheme.colors.primary}05`;
        }}
      >
        <Download
          size={48}
          color={disclaimerAcknowledged ? currentTheme.colors.primary : currentTheme.colors.textMuted}
          style={{ marginBottom: "16px" }}
        />
        <h3
          style={{
            color: currentTheme.colors.textPrimary,
            marginBottom: "8px",
            fontSize: "18px",
            fontWeight: "600",
          }}
        >
          {importFile
            ? importFile.name
            : disclaimerAcknowledged
            ? t("uploadFile")
            : isAr
            ? "أكّد الإشعار أعلاه أولاً"
            : "Acknowledge the notice above to continue"}
        </h3>
        <p
          style={{
            color: currentTheme.colors.textSecondary,
            marginBottom: "16px",
            fontSize: "14px",
          }}
        >
          {t("supportedFormats")}
        </p>
        {importFile && (
          <div
            style={{
              padding: "8px 16px",
              background: currentTheme.colors.success,
              color: "#fff",
              borderRadius: "6px",
              display: "inline-block",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            ✓ {(importFile.size / 1024).toFixed(2)} KB
          </div>
        )}
      </label>

      {importError && (
        <div style={modalStyles.errorBox}>
          <AlertCircle
            size={18}
            style={{ marginInlineEnd: "8px", verticalAlign: "middle" }}
          />
          <span style={{ fontSize: "14px", fontWeight: "500" }}>
            {importError}
          </span>
        </div>
      )}

      {importLoading && (
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <Activity
            size={32}
            color={currentTheme.colors.primary}
            style={{ animation: "spin 1s linear infinite" }}
          />
          <p
            style={{
              color: currentTheme.colors.textSecondary,
              marginTop: "12px",
              fontSize: "14px",
            }}
          >
            {t("processingFile")}
          </p>
        </div>
      )}

      {/* ── Import settings ── */}
      <div
        style={{
          marginTop: "20px",
          padding: "16px",
          background: currentTheme.colors.inputBackground,
          borderRadius: "10px",
          border: `1px solid ${currentTheme.colors.border}`,
        }}
      >
        <h4
          style={{
            color: currentTheme.colors.textPrimary,
            marginBottom: "12px",
            fontSize: "15px",
            fontWeight: "600",
          }}
        >
          {t("importSettings")}
        </h4>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: currentTheme.colors.textSecondary,
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            <input
              type="checkbox"
              checked={importSettings.skipErrors}
              onChange={(e) =>
                setImportSettings({ ...importSettings, skipErrors: e.target.checked })
              }
            />
            {t("skipErrors")}
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: currentTheme.colors.textSecondary,
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            <input
              type="checkbox"
              checked={importSettings.calculateMissing}
              onChange={(e) =>
                setImportSettings({ ...importSettings, calculateMissing: e.target.checked })
              }
            />
            {t("autoCalculate")}
          </label>
        </div>
      </div>
    </div>
  );
};

export default UploadStep;